import { Link } from "@tanstack/react-router"
import { ChevronDown, FileInput, GitCommitVertical, Route as RouteIcon, ScrollText, Sparkles } from "lucide-react"
import { type ReactNode, useMemo, useState } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { refusalOfError } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { attributeField, historyWithheld } from "@/lib/attribution"
import { useProvenance, useWhy } from "@/lib/queries"
import { cn } from "@/lib/utils"
import { localName, sentenceCase } from "./humanize"
import { ReceiptList, receiptsOf } from "@/data/receipts"
import { useOntology } from "./hooks"
import { HowItWorks } from "./how-it-works"
import type { FieldMeta, Ontology, TypeMeta } from "./ontology"
import { ReferenceName } from "./related"
import { FieldValue, isMasked, type Masked, RefusalNote, useFormatters } from "./values"

/** `showcase.Order.create` → `Create order` (verb first, the type in lower case). */
export function actionLabel(actionId: string | null | undefined, types: ReadonlyMap<string, TypeMeta>): string {
  if (!actionId) return ""
  const parts = actionId.split(".")
  const verb = parts.pop() ?? actionId
  const typeName = parts.join(".")
  const type = types.get(typeName)
  const noun = type ? type.label.toLowerCase() : sentenceCase(localName(typeName)).toLowerCase()
  return `${sentenceCase(verb)} ${noun}`.trim()
}

interface Hop {
  readonly icon: ReactNode
  readonly label: string
  readonly detail?: string
  readonly mono?: string
}

function commitNumber(commit: string): string {
  return commit.replace(/^commit:/, "").replace(/^main\//, "#")
}

/** The provenance section of a record: per field, where its current value came from. */
function subjectOf(step: { key?: unknown }): string | null {
  const key = step.key as { objectType?: unknown; key?: unknown } | null | undefined
  return key && typeof key.objectType === "string" && typeof key.key === "string" ? `object:${key.objectType}/${key.key}` : null
}

export function ProvenanceSection({
  scope,
  type,
  types,
  objectKey,
  values,
  masked,
  version,
}: {
  scope: Scope
  type: TypeMeta | undefined
  types: ReadonlyMap<string, TypeMeta>
  objectKey: string
  values: Record<string, unknown>
  masked: Masked
  version: number
}) {
  const t = useT()
  const fmt = useFormatters()
  const objectType = type?.name ?? ""
  const { ontology } = useOntology(scope)
  const why = useWhy(scope, objectType, objectKey)
  const provenance = useProvenance(scope, objectType, objectKey, Boolean(type))
  const times = useMemo(() => new Map((why.data?.nodes ?? []).map((n) => [n.id, n.recordedAt])), [why.data])
  const actions = (why.data?.steps ?? []).filter((s) => s.kind === "action")

  /** Hops for one field from the shared attribution: recorded evidence, policy, or the writing call. */
  const attributionFor = (field: FieldMeta): { hops: Hop[] | null; policyHidden: boolean } => {
    const attribution = attributeField(field.id, values[field.id], version, why.data, provenance.data)
    if (attribution.kind === "hidden") return { hops: null, policyHidden: true }
    if (attribution.kind === "source") {
      const recorded = attribution.evidence
      return {
        policyHidden: false,
        hops: [
          { icon: <FileInput />, label: t("ws.why.sourceDocument"), detail: recorded.source.system, mono: recorded.source.record },
          { icon: <ScrollText />, label: t("ws.why.observed"), detail: fmt.date(recorded.observedAt ?? recorded.recordedAt) },
          { icon: <RouteIcon />, label: t("ws.why.mapping"), mono: recorded.transform ? `${recorded.mappingVersion} · ${recorded.transform}` : recorded.mappingVersion },
        ],
      }
    }
    if (attribution.kind === "action") {
      const step = attribution.step
      return {
        policyHidden: false,
        hops: [
          { icon: <Sparkles />, label: t("ws.why.actionInput"), detail: actionLabel(step.actionId, types), mono: step.actor ? t("ws.why.byActor", { actor: step.actor }) : undefined },
          { icon: <GitCommitVertical />, label: t("ws.why.committed"), detail: attribution.recordedAt ? fmt.date(attribution.recordedAt) : undefined, mono: commitNumber(step.commit) },
        ],
      }
    }
    return { hops: null, policyHidden: false }
  }

  const [all, setAll] = useState(false)
  const every = (type?.fields ?? []).filter((f) => f.role !== "technical" && f.role !== "identifier")
  const fields = all ? every : every.slice(0, 6)
  const refused = why.isError ? refusalOfError(why.error) : null
  // "Created by" only from this subject's own `created` fact, bound to the call in the same commit.
  const createdFact = (why.data?.steps ?? []).find((s) => s.kind === "created" && subjectOf(s) === why.data?.subject)
  const created = createdFact ? actions.find((a) => a.commit === createdFact.commit) : undefined
  // Connector-ingested values carry recorded source evidence: one receipt per source record.
  const receipts = receiptsOf(provenance.data, version)

  return (
    <section aria-labelledby="ws-why" className="space-y-4">
      <div className="flex items-center gap-2">
        <span aria-hidden className="h-4 w-0 border-s-2 border-dashed border-[var(--lineage)]" />
        <h2 id="ws-why" className="text-[15px] font-semibold tracking-tight">
          {t("ws.why.title")}
        </h2>
        <HowItWorks lines={["ws.how.why"]} />
        <Link
          to="/$orgId/$environmentId/objects/$objectType/$key"
          params={{ ...scope, objectType, key: objectKey }}
          className="ms-auto text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {t("ws.why.fullLineage")}
        </Link>
      </div>
      {why.isPending ? (
        <Skeleton className="h-16 w-full rounded-xl" />
      ) : why.isError ? (
        <RefusalNote error={why.error} title={refused && (refused.refusal.category === "permission" || refused.refusal.category === "visibility") ? t("ws.why.hidden") : (refused?.refusal.message ?? t("ws.why.unavailable"))} />
      ) : historyWithheld(why.data) ? (
        <p className="text-sm text-muted-foreground">{t("objects.historyHidden")}</p>
      ) : created ? (
        <p className="text-sm text-muted-foreground">
          {t("ws.why.createdBy", {
            action: actionLabel(created.actionId, types),
            actor: created.actor ?? "—",
            when: times.get(created.commit) ? fmt.date(times.get(created.commit) as string) : "—",
          })}
          {why.data?.historyTruncated ? <span className="text-subtle-foreground"> · {t("ws.why.truncated")}</span> : null}
        </p>
      ) : null}
      {provenance.isError ? <RefusalNote error={provenance.error} title={t("ws.why.evidenceUnavailable")} /> : null}
      {receipts.length ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">{t("dv.receipt.title")}</p>
          <ReceiptList receipts={receipts} labelOf={(id) => type?.fields.find((f) => f.id === id)?.label ?? id} />
        </div>
      ) : null}
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
        {fields.map((field) => (
          <FieldProvenance key={field.id} scope={scope} ontology={ontology} field={field} type={type} values={values} masked={masked} {...(why.isSuccess ? attributionFor(field) : { hops: null, policyHidden: false })} pending={why.isPending} />
        ))}
      </ul>
      {every.length > 6 ? (
        <button type="button" onClick={() => setAll((v) => !v)} className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          {all ? t("ws.related.showFewer") : t("ws.why.allFields", { count: every.length })}
        </button>
      ) : null}
    </section>
  )
}

function FieldProvenance({
  scope,
  ontology,
  field,
  type,
  values,
  masked,
  hops,
  policyHidden,
  pending,
}: {
  scope: Scope
  ontology: Ontology | undefined
  field: FieldMeta
  type: TypeMeta | undefined
  values: Record<string, unknown>
  masked: Masked
  hops: Hop[] | null
  policyHidden: boolean
  pending: boolean
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const hidden = isMasked(masked, field.id)
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        disabled={!hops}
        onClick={() => setOpen((v) => !v)}
        className="grid w-full grid-cols-[minmax(8rem,12rem)_1fr_auto] items-center gap-4 px-4 py-2.5 text-start outline-none transition-colors hover:bg-muted/40 focus-visible:bg-muted/50 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="truncate text-[13px] text-muted-foreground">{field.label}</span>
        <span className="min-w-0 truncate text-sm">
          {field.role === "reference" && type && !isMasked(masked, field.id) ? (
            <ReferenceName scope={scope} type={type} field={field} value={values[field.id]} ontology={ontology} />
          ) : (
            <FieldValue field={field} values={values} masked={masked} type={type} />
          )}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-[var(--lineage)]">
          {pending ? (
            <Skeleton className="h-3 w-20" />
          ) : hops ? (
            <>
              <span className="hidden sm:inline">{hops[0]?.label}</span>
              <ChevronDown className={cn("size-3.5 transition-transform duration-200", open && "rotate-180")} aria-hidden />
            </>
          ) : (
            <span className="text-subtle-foreground">{t(policyHidden ? "objects.historyHidden" : "ws.why.noSource")}</span>
          )}
        </span>
      </button>
      {open && hops ? (
        <ol className="flex flex-wrap items-stretch gap-y-2 px-4 pb-4" aria-label={t("ws.why.chainFor", { field: field.label })}>
          {hops.map((hop, i) => (
            <li key={hop.label} className="flex items-center">
              {i > 0 ? <span aria-hidden className="mx-1.5 w-6 border-t border-dashed border-[var(--lineage)]/70" /> : null}
              <HopCard hop={hop} />
            </li>
          ))}
          <li className="flex items-center">
            <span aria-hidden className="mx-1.5 w-6 border-t border-dashed border-[var(--lineage)]/70" />
            <div className="rounded-xl border border-[var(--lineage)]/40 bg-[var(--lineage)]/8 px-3 py-2">
              <p className="text-2xs text-[var(--lineage)]">{t("ws.why.thisField")}</p>
              <p className="text-sm">{hidden ? t("ws.hiddenByPolicy") : <FieldValue field={field} values={values} masked={masked} type={type} />}</p>
            </div>
          </li>
        </ol>
      ) : null}
    </li>
  )
}

function HopCard({ hop }: { hop: Hop }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-dashed border-[var(--lineage)]/50 px-3 py-2 [&_svg]:mt-0.5 [&_svg]:size-3.5 [&_svg]:text-[var(--lineage)]">
      {hop.icon}
      <div className="min-w-0">
        <p className="text-2xs text-muted-foreground">{hop.label}</p>
        {hop.detail ? <p className="text-[13px]">{hop.detail}</p> : null}
        {hop.mono ? (
          <p dir="ltr" className="font-mono text-2xs text-muted-foreground">
            {hop.mono}
          </p>
        ) : null}
      </div>
    </div>
  )
}

